import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/component/navbar';
import Dh from '@/components/Dh';
import { useSetupPricing } from '@/data/setupPricing';

type Choice = 'compliance' | 'no-thanks';

const RadioCircle = ({ selected }: { selected: boolean }) => (
  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${selected ? 'border-amber-500' : 'border-gray-400'}`}>
    {selected && <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />}
  </div>
);

export default function SetupUpgrade() {
  const [selected, setSelected] = useState<Choice>('compliance');
  const navigate = useNavigate();
  const P = useSetupPricing();

  const handleNext = () => {
    localStorage.setItem('setupUpgrade', selected);
    navigate('/setup-invoice');
  };

  // Title, price and note track setupPricing/current so this upsell can't disagree with
  // what SetupInvoice actually charges (P.upgradePrice) or with the plan's edited name.
  const compliance = P.setupPlans.find(p => p.id === 'compliance');

  const cards: { id: Choice; title: string; description: string; price?: string; billing?: string; note?: string }[] = [
    {
      id: 'compliance',
      title: compliance?.title || 'Total Compliance',
      description: compliance?.description || 'For founders who want to stay 100% compliant with UAE regulations.',
      price: P.upgradePrice.toLocaleString(),
      billing: '/yr',
      note: compliance?.priceNote || '(+ government fees)',
    },
    {
      id: 'no-thanks',
      title: 'No Thanks',
      description: "I'd rather handle my company compliance on my own.",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <Navbar hideSignBtn={false} />
      <div className="flex-1 px-8 py-16 max-w-5xl mx-auto w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-10 max-w-lg leading-snug">
          Get your Tax Number and more included when you upgrade to Total Compliance
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {cards.map((card) => {
            const isSelected = selected === card.id;
            return (
              <div
                key={card.id}
                onClick={() => setSelected(card.id)}
                className="rounded-xl p-6 cursor-pointer flex flex-col transition-all duration-200"
                style={{
                  backgroundColor: isSelected ? '#FFFBEB' : '#F9FAFB',
                  border: isSelected ? '2px solid #F59E0B' : '2px solid #E5E7EB',
                }}
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-sm text-gray-600 mb-8 leading-relaxed">{card.description}</p>
                <div className="flex items-end justify-between mt-auto">
                  <div>
                    {card.price && (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-gray-900 inline-flex items-baseline"><Dh em="0.8" />{card.price}</span>
                          <span className="text-sm text-gray-700">{card.billing}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{card.note}</p>
                      </>
                    )}
                  </div>
                  <RadioCircle selected={isSelected} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-8 py-3 border-2 border-gray-300 rounded-lg font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            ← Back
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-lg hover:bg-amber-500 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
